import React, { useEffect, useRef, useState } from 'react';
import { Application, Sprite, Container, Graphics, Assets, Text } from 'pixi.js';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { wsClient } from '@/lib/ws';
import { backendUrl } from '@/lib/bubbleImage';
import { Users, Microphone, X } from '@phosphor-icons/react';

/**
 * LiveSky — the metaverse moment.
 *
 * When ≥2 bubbles have RSVP'd in-person to a Lighthouse and we're inside
 * its window, this canvas takes over the top of LighthouseDetail and
 * renders a shared 2.5D sky where every present bubble drifts in real
 * time. Authoritative position state comes from the server's 4Hz tick;
 * the client interpolates smoothly between snapshots.
 *
 * Voice particles appear as pulsing orbs you can tap to listen to.
 * Bubble-bubble collisions register a `sky_brush` ledger event.
 */
const LiveSky = ({ lighthouse, bubbleId }) => {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const spritesRef = useRef({}); // bubble_id -> { sprite, target_x, target_y }
  const particlesRef = useRef({}); // particle_id -> Graphics
  const [occupants, setOccupants] = useState(0);
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);

  // 1. Initialise the pixi app once
  useEffect(() => {
    if (!containerRef.current || appRef.current) return undefined;
    let cancelled = false;
    const init = async () => {
      const app = new Application();
      await app.init({
        backgroundAlpha: 0,
        antialias: true,
        resizeTo: containerRef.current,
        autoDensity: true,
        resolution: window.devicePixelRatio || 1,
      });
      if (cancelled) { app.destroy(true); return; }
      containerRef.current.appendChild(app.canvas);
      app.canvas.style.width = '100%';
      app.canvas.style.height = '100%';
      app.canvas.style.display = 'block';
      app.canvas.style.borderRadius = 'inherit';
      appRef.current = app;

      // Smooth interpolation tick
      app.ticker.add((tk) => {
        const w = app.screen.width, h = app.screen.height;
        Object.values(spritesRef.current).forEach((s) => {
          if (!s.sprite) return;
          const tx = (s.target_x ?? 0.5) * w;
          const ty = (s.target_y ?? 0.5) * h;
          s.sprite.x += (tx - s.sprite.x) * 0.05;
          s.sprite.y += (ty - s.sprite.y) * 0.05;
        });
      });
    };
    init();
    return () => {
      cancelled = true;
      try { appRef.current?.destroy(true, { children: true }); } catch { /* */ }
      appRef.current = null;
      spritesRef.current = {};
      particlesRef.current = {};
    };
  }, []);

  // 2. Join the sky on mount, leave on unmount
  useEffect(() => {
    if (!bubbleId || !lighthouse?.id) return undefined;
    let mounted = true;
    setJoining(true);
    api.post(`/lighthouses/${lighthouse.id}/sky/join`, { bubble_id: bubbleId })
      .then(({ data }) => {
        if (!mounted) return;
        setJoined(true);
        setOccupants(data.occupants || 1);
      })
      .catch((err) => toast.error(err?.response?.data?.detail || 'could not enter the sky'))
      .finally(() => mounted && setJoining(false));

    return () => {
      mounted = false;
      api.post(`/lighthouses/${lighthouse.id}/sky/leave`, { bubble_id: bubbleId }).catch(() => {});
    };
  }, [lighthouse?.id, bubbleId]);

  // 3. Poll initial state + listen to WS for live updates
  useEffect(() => {
    if (!joined || !lighthouse?.id) return undefined;
    let stop = false;
    const seedSprites = async (bubbles) => {
      const app = appRef.current;
      if (!app) return;
      const present = new Set(bubbles.map((b) => b.bubble_id || b.id));
      // Remove gone
      Object.keys(spritesRef.current).forEach((bid) => {
        if (!present.has(bid)) {
          try { app.stage.removeChild(spritesRef.current[bid].sprite); } catch { /* */ }
          delete spritesRef.current[bid];
        }
      });
      // Add or update
      for (const b of bubbles) {
        const bid = b.bubble_id || b.id;
        if (!spritesRef.current[bid]) {
          const c = new Container();
          let texture = null;
          if (b.image_url) {
            try { texture = await Assets.load(backendUrl(b.image_url)); }
            catch { /* fallback below */ }
          }
          const sprite = texture ? new Sprite(texture) : new Graphics().circle(0, 0, 24).fill({ color: 0x7d6dff, alpha: 0.8 });
          if (texture) {
            sprite.anchor.set(0.5);
            const size = 56 * (b.d ?? b.depth ?? 1);
            sprite.width = size; sprite.height = size;
          } else {
            sprite.scale.set((b.d ?? b.depth ?? 1));
          }
          // soft glow ring behind
          const glow = new Graphics().circle(0, 0, 38).fill({ color: 0x00f0ff, alpha: 0.15 });
          c.addChild(glow);
          c.addChild(sprite);
          // Bubble name tag
          if (b.bubble_name) {
            const tag = new Text({
              text: b.bubble_name,
              style: { fontFamily: 'Inter', fontSize: 10, fill: 0xffffff, fontWeight: '700' },
            });
            tag.anchor.set(0.5);
            tag.y = 36;
            tag.alpha = 0.6;
            c.addChild(tag);
          }
          spritesRef.current[bid] = { sprite: c, target_x: b.x, target_y: b.y };
          c.x = (b.x ?? 0.5) * app.screen.width;
          c.y = (b.y ?? 0.5) * app.screen.height;
          app.stage.addChild(c);
        } else {
          const s = spritesRef.current[bid];
          s.target_x = b.x; s.target_y = b.y;
        }
      }
    };

    const pull = async () => {
      try {
        const { data } = await api.get(`/lighthouses/${lighthouse.id}/sky`);
        if (stop) return;
        await seedSprites(data.bubbles || []);
        setOccupants((data.bubbles || []).length);
        renderParticles(data.voice_particles || []);
      } catch { /* */ }
    };

    const renderParticles = (list) => {
      const app = appRef.current;
      if (!app) return;
      const w = app.screen.width, h = app.screen.height;
      const present = new Set(list.map((p) => p.id));
      Object.keys(particlesRef.current).forEach((pid) => {
        if (!present.has(pid)) {
          try { app.stage.removeChild(particlesRef.current[pid]); } catch { /* */ }
          delete particlesRef.current[pid];
        }
      });
      for (const p of list) {
        if (particlesRef.current[p.id]) continue;
        const g = new Graphics().circle(0, 0, 14).fill({ color: 0xccff00, alpha: 0.55 });
        const ring = new Graphics().circle(0, 0, 18).stroke({ color: 0xccff00, width: 2, alpha: 0.7 });
        const c = new Container();
        c.addChild(ring); c.addChild(g);
        c.x = p.x * w; c.y = p.y * h;
        c.eventMode = 'static';
        c.cursor = 'pointer';
        c.on('pointertap', () => {
          try {
            const a = new Audio(backendUrl(p.audio_url));
            a.play().catch(() => {});
            toast.message(`a voice from ${p.bubble_name}`);
          } catch { /* */ }
        });
        // gentle pulse
        let t = 0;
        app.ticker.add(() => { t += 0.05; c.scale.set(1 + Math.sin(t) * 0.07); });
        app.stage.addChild(c);
        particlesRef.current[p.id] = c;
      }
    };

    pull();
    const t = setInterval(pull, 4000);
    const off = wsClient.onEvent((m) => {
      if (m?.type === 'sky_state' && m.payload?.lighthouse_id === lighthouse.id) {
        (m.payload.bubbles || []).forEach((b) => {
          const s = spritesRef.current[b.id];
          if (s) { s.target_x = b.x; s.target_y = b.y; }
        });
      }
      if (m?.type === 'sky_brush' && m.payload?.lighthouse_id === lighthouse.id) {
        toast.message('two bubbles just brushed in the sky ✦', { duration: 2500 });
      }
      if (m?.type === 'sky_voice_dropped' && m.payload?.lighthouse_id === lighthouse.id) {
        pull(); // re-fetch particles
      }
      if (m?.type === 'sky_joined' && m.payload?.lighthouse_id === lighthouse.id) {
        setOccupants(m.payload.occupants || occupants);
        pull();
      }
    });
    return () => { stop = true; clearInterval(t); off(); };
  }, [joined, lighthouse?.id]); // eslint-disable-line

  // Voice drop UI
  const recordingRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = async () => {
          // Upload via existing cargo path: just POST b64 to /sky/voice as a data URL
          const audio_url = reader.result; // we'll send as data: URL for simplicity
          try {
            await api.post(`/lighthouses/${lighthouse.id}/sky/voice`, {
              bubble_id: bubbleId,
              audio_url,
              x: 0.5 + (Math.random() - 0.5) * 0.4,
              y: 0.5 + (Math.random() - 0.5) * 0.4,
            });
          } catch (err) { toast.error('could not drop voice'); }
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recordingRef.current = rec;
      setRecording(true);
      setTimeout(() => stopRecord(), 8000); // max 8s
    } catch (err) { toast.error('mic permission denied'); }
  };
  const stopRecord = () => {
    if (recordingRef.current && recordingRef.current.state !== 'inactive') {
      recordingRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="relative" data-testid="live-sky">
      <div ref={containerRef}
        className="aspect-[16/9] rounded-3xl overflow-hidden relative"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(125,109,255,0.18) 0%, rgba(0,0,0,0.95) 75%)',
        }}>
      </div>
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none">
        <div className="px-3 py-1.5 rounded-full glass-hi text-[10px] uppercase tracking-widest text-bx-cyan font-bold flex items-center gap-1.5 pointer-events-auto">
          <Users size={11} weight="duotone" /> {occupants} in the sky · live
        </div>
        {joined && (
          <button
            data-testid="sky-voice-btn"
            onMouseDown={startRecord} onMouseUp={stopRecord}
            onTouchStart={startRecord} onTouchEnd={stopRecord}
            className={`pointer-events-auto px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${
              recording ? 'bg-bx-magenta text-black' : 'bg-bx-volt/80 text-black'
            }`}>
            <Microphone size={11} weight="fill" />
            {recording ? 'release' : 'hold to drop voice'}
          </button>
        )}
      </div>
      {joining && (
        <div className="absolute inset-0 grid place-items-center bg-black/40 backdrop-blur-sm pointer-events-none">
          <div className="text-white/70 text-xs uppercase tracking-widest">entering the sky…</div>
        </div>
      )}
    </div>
  );
};

export default LiveSky;
